import { useState, useEffect, useCallback, useRef, Fragment, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flame, ChevronRight, Loader2, Film, Sparkles, Plus, Coins, Settings } from "lucide-react";
import { Sparkle } from "@/components/icons";
import api from "@/lib/api";
import { optimizeImg, mediaSrc } from "@/lib/img";
import { useAuth } from "@/context/AuthContext";
import { LOGO_MARK } from "@/lib/brand";
import BottomNav from "@/components/BottomNav";
import PostCard from "@/components/PostCard";
import LoopVideo from "@/components/LoopVideo";
import AstroStudioModal from "@/components/AstroStudioModal";
import PostComposer from "@/components/PostComposer";

const TABS = [{ v: "for_you", l: "For You" }, { v: "following", l: "Following" }, { v: "trending", l: "Trending" }];

function TemplatesRail({ templates, navigate }) {
  if (!templates.length) return null;
  return (
    <section data-testid="explore-templates" className="py-1">
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="flex items-center gap-2"><Flame size={20} className="text-gold" />
          <h2 className="font-heading font-black uppercase tracking-tight text-lg sm:text-2xl">Explore AI Templates</h2></div>
        <ChevronRight size={20} className="text-zinc-500" />
      </div>
      <p className="text-[#A89F94] text-xs sm:text-sm mb-3 px-1">Pick a look, upload your photo, tap once, that's it.</p>
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 snap-x">
        {templates.map((t) => (
          <button key={t.id} onClick={() => navigate(`/t/${t.id}`)} data-testid={`template-card-${t.id}`}
            className="group relative shrink-0 w-[38vw] max-w-[180px] sm:w-44 snap-start rounded-xl overflow-hidden border border-white/10 bg-ink-900 text-left hover:border-gold/60 transition-colors">
            <div className="relative aspect-[3/4] overflow-hidden bg-ink-950">
              {t.preview_video ? <LoopVideo src={mediaSrc(t.preview_video)} autoplay={!!t.loop_on_home} className="w-full h-full object-cover" />
                : <img src={optimizeImg(t.thumbnail, 360, 68)} alt={t.title} decoding="async" className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-transparent to-transparent" />
              <div className="absolute top-2 left-2 flex gap-1">
                {t.trending && <span className="bg-gold text-ink-950 text-[9px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><Flame size={9} /> HOT</span>}
                {t.output_type === "video" && <span className="bg-ink-950/80 text-[9px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><Film size={9} /> Video</span>}
              </div>
              <div className="absolute bottom-2 left-2.5 right-2.5"><div className="font-heading font-bold text-sm text-white drop-shadow truncate">{t.title}</div></div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
const MemoTemplatesRail = memo(TemplatesRail);

export default function Feed() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [railTemplates, setRailTemplates] = useState([]);
  const [tab, setTab] = useState("for_you");
  const [posts, setPosts] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [compose, setCompose] = useState(false);
  const [astroOpen, setAstroOpen] = useState(false);
  const sentinel = useRef(null);
  const seen = useRef(new Set());
  const cursor = useRef(null);

  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      const all = data.templates || [];
      setTemplates(all);
      setRailTemplates([...all].sort(() => Math.random() - 0.5).slice(0, 5));
    }).catch(() => {});
  }, []);
  useEffect(() => { if (params.get("create") === "astro") setAstroOpen(true); }, [params]);

  const loadMore = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    const curSkip = reset ? 0 : skip;
    const page = tab === "trending" || reset || !cursor.current ? `skip=${curSkip}` : `before=${encodeURIComponent(cursor.current)}`;
    try {
      const { data } = await api.get(`/feed?tab=${tab}&${page}&limit=6`);
      const got = data.posts || [];
      const fresh = got.filter((p) => reset || !seen.current.has(p.id));
      fresh.forEach((p) => seen.current.add(p.id));
      setPosts((prev) => (reset ? fresh : [...prev, ...fresh]));
      if (data.next_before) cursor.current = data.next_before;
      setSkip(curSkip + got.length);
      setHasMore(!!data.has_more);
    } catch { setHasMore(false); }
    finally { setLoading(false); }
  }, [tab, skip, loading]);

  useEffect(() => { seen.current = new Set(); cursor.current = null; setPosts([]); setSkip(0); setHasMore(true); loadMore(true); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = sentinel.current; if (!el) return;
    const obs = new IntersectionObserver((e) => { if (e[0].isIntersecting && hasMore && !loading) loadMore(); }, { rootMargin: "600px" });
    obs.observe(el); return () => obs.disconnect();
  }, [hasMore, loading, loadMore]);

  const chunks = [];
  for (let i = 0; i < posts.length; i += 6) chunks.push(posts.slice(i, i + 6));
  const handleDeleted = useCallback((id) => setPosts((x) => x.filter((q) => q.id !== id)), []);

  return (
    <div className="w-full min-h-screen bg-ink-950 text-[#F0E9DD] pb-24 overflow-x-hidden">
      {/* header */}
      <header className="sticky top-0 z-40 bg-ink-950/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={LOGO_MARK} alt="" className="w-7 h-7 object-contain" />
            <span className="font-heading font-bold tracking-tight text-lg">nosey<span className="text-gold"> ai</span></span>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && <button onClick={() => navigate("/admin")} data-testid="feed-admin" className="border border-gold/40 bg-[#1a140d] rounded-full p-2 hover:border-gold"><Settings size={15} className="text-gold" /></button>}
            <button onClick={() => navigate("/wallet")} data-testid="feed-coins" className="flex items-center gap-1.5 border border-yellow-500/40 bg-[#1a140d] rounded-full px-2.5 py-1.5 hover:border-yellow-500">
              <Coins size={14} className="text-yellow-400" /><span className="font-mono text-xs text-yellow-300 font-semibold">{Math.round(user?.spendable_coins ?? user?.coin_balance ?? 0)}</span></button>
            <button onClick={() => navigate("/pricing")} data-testid="feed-credits" className="flex items-center gap-1.5 border border-gold/40 bg-[#1a140d] rounded-full px-2.5 py-1.5 hover:border-gold">
              <Sparkle size={13} className="text-gold" /><span className="font-mono text-xs text-gold font-semibold">{user?.role === "admin" ? "∞" : (user?.credits ?? 0)}</span></button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 pt-4">
        <MemoTemplatesRail templates={railTemplates} navigate={navigate} />

        {/* tabs */}
        <div className="sticky top-14 z-30 -mx-3 sm:-mx-4 px-3 sm:px-4 bg-ink-950/90 backdrop-blur-xl border-b border-white/5 flex items-center gap-6 mt-4 mb-3">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`feed-tab-${t.v}`}
              className={`relative py-3 font-heading font-bold text-sm transition-colors ${tab === t.v ? "text-gold" : "text-zinc-500 hover:text-zinc-300"}`}>
              {t.l}{tab === t.v && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-gold rounded" />}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {chunks.map((chunk, ci) => (
            <Fragment key={ci}>
              {chunk.map((p) => <PostCard key={p.id} post={p} onDeleted={handleDeleted} />)}
            </Fragment>
          ))}

          {loading && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gold" size={26} /></div>}
          {!loading && posts.length === 0 && (
            <div className="border border-dashed border-zinc-800 rounded-xl py-20 flex flex-col items-center text-center" data-testid="feed-empty">
              <Sparkles size={36} className="text-zinc-700 mb-3" />
              <p className="text-zinc-400">{tab === "following" ? "Follow creators to see their posts here." : "No posts yet."}</p>
            </div>
          )}
          <div ref={sentinel} className="h-8" />
        </div>
      </main>

      {/* compose — green square, distinct from the gold nav plus */}
      <button onClick={() => setCompose(true)} data-testid="feed-compose" className="fixed bottom-24 right-5 z-40 w-14 h-14 rounded-2xl bg-[#22c55e] text-ink-950 flex items-center justify-center shadow-2xl shadow-black/50 hover:bg-[#26d466] transition-colors"><Plus size={26} /></button>
      {compose && <PostComposer onClose={() => setCompose(false)} onPosted={() => { setCompose(false); window.scrollTo({ top: 0 }); if (tab !== "for_you") setTab("for_you"); else { seen.current = new Set(); cursor.current = null; loadMore(true); } }} />}
      <AstroStudioModal open={astroOpen} onOpenChange={(o) => { setAstroOpen(o); if (!o && params.get("create")) { params.delete("create"); setParams(params, { replace: true }); } }} />

      <BottomNav />
    </div>
  );
}
